import React, { useContext } from 'react';
import { animated, useTransition } from 'react-spring';

import { EditableFieldsContext } from '../context';

import { noop } from 'common/utils';

export interface EditableFieldProps {
    isEditing: boolean;
    editMode: boolean;
    FieldDisplayComponent: React.ElementType;
    FieldEditComponent: React.ElementType;
    className?: string;
}

export const EditableField = <T extends EditableFieldProps>({
    editMode,
    isEditing,
    className,
    FieldDisplayComponent,
    FieldEditComponent,
    ...props
}: T) => {
    const transitions = useTransition(isEditing, {
        from: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
        enter: { opacity: 1, transform: 'translate2d(0%, 100%)', maxHeight: '1000px' },
        leave: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
    });

    return (
        <div className={className}>
            <div className={editMode && !isEditing ? 'opacity-20' : 'opacity-100'}>
                <FieldDisplayComponent isEditing={isEditing} {...props} />
            </div>
            {transitions((styles, item) => {
                return (
                    item && (
                        <animated.div style={styles}>
                            <FieldEditComponent isEditing={isEditing} {...props} />
                        </animated.div>
                    )
                );
            })}
        </div>
    );
};

export interface FieldProps extends EditableFieldProps {
    id: string;
}

export interface ToggleEditViewRendererProps {
    isEditing: boolean;
    onClick?: React.MouseEventHandler<HTMLParagraphElement>;
}

export const ToggleEditViewRenderer = ({ isEditing, onClick }: ToggleEditViewRendererProps) => (
    <p onClick={onClick} className="font-semibold underline text-brand text-xs cursor-pointer">
        {isEditing ? 'Cancel' : 'Edit'}
    </p>
);

export const LabelRenderer = ({ label }: { label: React.ReactNode }) => (
    <label className="text-grey-2 text-sm">{label}</label>
);

export interface BaseFieldDisplayProps {
    label: React.ReactNode;
    isEditing: boolean;
    id: string;
    children: React.ReactNode;
    onClick: () => void;
    renderers?: { [key: string]: React.ElementType };
}

const defaultRenderers = {
    ToggleEditViewRenderer,
    LabelRenderer,
};

export const BaseFieldDisplay = ({
    label,
    isEditing,
    id,
    children,
    onClick,
    renderers = {},
}: BaseFieldDisplayProps) => {
    const { fieldToEdit } = useContext(EditableFieldsContext);
    const components = { ...defaultRenderers, ...renderers };

    const notBeingEdited = fieldToEdit && id !== fieldToEdit;

    return (
        <div className="flex flex-col">
            <div className="flex justify-between mb-2.5">
                <components.LabelRenderer label={label} />
                <components.ToggleEditViewRenderer
                    isEditing={isEditing}
                    onClick={notBeingEdited ? undefined : onClick}
                />
            </div>

            <div className="text-lg text-brand-dark">{children}</div>
        </div>
    );
};

interface FieldDisplayProps extends Omit<BaseFieldDisplayProps, 'onClick'> {
    onToggle?: () => void;
}

export const FieldDisplay = ({ onToggle = noop, ...props }: FieldDisplayProps) => {
    const { fieldToEdit, setFieldToEdit } = useContext(EditableFieldsContext);

    const onClick = () => {
        setFieldToEdit(fieldToEdit === props.id ? '' : props.id);
        onToggle();
    };

    return <BaseFieldDisplay {...props} onClick={onClick} />;
};
